import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as FRAGS from "@thatopen/fragments";
import "./style.css";

const IFC_NAME_RE = /^[A-Za-z0-9._-]+\.ifc$/i;
const IDB_NAME = "ecc-bim-frag-cache";
const IDB_STORE = "frags";
const IDB_VERSION = 1;
/** Multi-color assembly highlight styles (ecc-hl-0 … ecc-hl-n + root). */
const ECC_HL_PREFIX = "ecc-hl-";
const ECC_HL_ROOT = "ecc-hl-root";
/** Distinct bright colors for dark steel backgrounds (not all blue). */
const HL_PALETTE = [
  "#ff5c33", // orange-red
  "#ffcc00", // gold
  "#33dd66", // green
  "#ff66cc", // pink
  "#66aaff", // sky
  "#ff9933", // amber
  "#aaff33", // lime
  "#cc66ff", // purple
  "#33eedd", // teal
  "#ff4466", // rose
] as const;

const MARK_KEYS = new Set([
  "tag",
  "mark",
  "assembly_mark",
  "assemblymark",
  "piece_mark",
  "piecemark",
  "piece mark",
  "member_mark",
  "part_mark",
]);

const CATEGORY_LABELS: Record<string, string> = {
  COLUMN: "Columns",
  WALL: "Walls",
  WALLSTANDARDCASE: "Walls",
  BEAM: "Beams",
  SLAB: "Slabs",
  MEMBER: "Members",
  PLATE: "Plates",
  FOOTING: "Footings",
  PILE: "Piles",
  DOOR: "Doors",
  WINDOW: "Windows",
  CURTAINWALL: "Curtain walls",
  RAILING: "Railings",
  STAIR: "Stairs",
  STAIRFLIGHT: "Stair flights",
  RAMP: "Ramps",
  ROOF: "Roofs",
  COVERING: "Coverings",
  FURNISHINGELEMENT: "Furnishings",
  FLOWTERMINAL: "Flow terminals",
  FLOWSEGMENT: "Flow segments",
  FLOWFITTING: "Flow fittings",
  DISTRIBUTIONELEMENT: "Distribution",
  BUILDINGELEMENTPROXY: "Proxies",
  OPENINGELEMENT: "Openings",
  SPACE: "Spaces",
  ANNOTATION: "Annotations",
  REINFORCINGBAR: "Rebar",
  REINFORCINGMESH: "Rebar mesh",
  FASTENER: "Fasteners",
  MECHANICALFASTENER: "Fasteners",
  DISCRETEACCESSORY: "Accessories",
  ELEMENTASSEMBLY: "Assemblies",
  GRID: "Grids",
};

const ATTR_LABELS: Record<string, string> = {
  Name: "Name",
  ObjectType: "Type",
  Tag: "Mark",
  Description: "Description",
  PredefinedType: "Subtype",
};

const HIDDEN_ATTR_KEYS = new Set([
  "ownerhistory",
  "objectplacement",
  "representation",
  "referencedby",
  "hasassociations",
  "hascoverings",
  "fillsvoids",
  "connectedto",
  "connectedfrom",
  "hasopenings",
  "isdefinedby",
  "haspropertysets",
  "decomposes",
  "isdecomposedby",
  "containedinspatialstructure",
  "containsElements",
  "containselements",
]);


/** Main axis tags: plain letters (A–D) or plain integers (1–6). */
const IFC_AXIS_MAIN_LETTER = /^[A-Za-z]+$/;
const IFC_AXIS_MAIN_NUM = /^\d+$/;

function isMainGridAxisTag(tag: string): boolean {
  const t = tag.trim();
  return IFC_AXIS_MAIN_LETTER.test(t) || IFC_AXIS_MAIN_NUM.test(t);
}

/** Steel-cyan palette for Fragments grid lines. */
const ECC_GRID_MAIN_COLOR = 0x8eb4c8;
const ECC_GRID_SEC_COLOR = 0x6b92a8;

/** Dispose a Fragments/ECC grid group (geometry + non-shared materials). Never throws. */
function disposeEccGrid(
  grid: THREE.Object3D | null,
  scene?: THREE.Object3D | null,
): void {
  if (!grid) return;
  try {
    scene?.remove(grid);
    grid.removeFromParent();
    grid.traverse((obj) => {
      const anyObj = obj as THREE.Mesh & THREE.Sprite & THREE.Line;
      const owned =
        !!obj.userData.eccBubbleOwned ||
        !!obj.userData.eccOwned ||
        !!obj.userData.eccDisposeGeometry;
      if (owned && anyObj.geometry) {
        anyObj.geometry.dispose();
      }
      if ((obj as THREE.Sprite).isSprite) {
        const mat = (obj as THREE.Sprite).material;
        try {
          mat.map?.dispose();
        } catch {
          /* ignore */
        }
        if (obj.userData.eccDisposeMaterial) {
          try {
            mat.dispose();
          } catch {
            /* ignore */
          }
        }
        return;
      }
      // Only dispose materials we created for bubbles / secondary clones / fallback lines
      if (obj.userData.eccDisposeMaterial) {
        const mat = (obj as THREE.Mesh).material as
          | THREE.Material
          | THREE.Material[]
          | undefined;
        if (mat) {
          for (const m of Array.isArray(mat) ? mat : [mat]) m.dispose();
        }
      }
    });
    const shared = grid.userData.eccSharedMats as THREE.Material[] | undefined;
    if (shared) {
      for (const m of shared) {
        try {
          m.dispose();
        } catch {
          /* ignore */
        }
      }
    }
  } catch (err) {
    console.warn("disposeEccGrid", err);
  }
}



/** Race a promise; resolve null on timeout (never rejects). */
function withTimeout<T>(
  p: Promise<T>,
  ms: number,
  label: string,
): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const t = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn(`${label} timed out after ${ms}ms`);
      resolve(null);
    }, ms);
    p.then(
      (v) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(t);
        resolve(v);
      },
      (err) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(t);
        console.warn(label, err);
        resolve(null);
      },
    );
  });
}

function lengthUnitToMeters(unit: string): number {
  const u = unit.toUpperCase().replace(/[^A-Z]/g, "");
  switch (u) {
    case "METRE":
    case "METER":
    case "M":
      return 1;
    case "MILLIMETRE":
    case "MILLIMETER":
    case "MM":
      return 0.001;
    case "CENTIMETRE":
    case "CENTIMETER":
    case "CM":
      return 0.01;
    case "INCH":
    case "INCHES":
      return 0.0254;
    case "FOOT":
    case "FEET":
    case "FT":
      return 0.3048;
    case "YARD":
      return 0.9144;
    default:
      return 1;
  }
}

function detectLengthUnitFromIfcText(text: string): string {
  try {
    const conv = text.match(
      /IFCCONVERSIONBASEDUNIT\s*\([^,]*,\s*\.LENGTHUNIT\.\s*,\s*'([^']+)'/i,
    );
    if (conv?.[1]) return conv[1];
    const siPref = text.match(
      /IFCSIUNIT\s*\([^,]*,\s*\.LENGTHUNIT\.\s*,\s*\.([A-Z]*)\.\s*,\s*\.([A-Z_]+)\./i,
    );
    if (siPref) {
      const pref = (siPref[1] || "").toUpperCase();
      const name = (siPref[2] || "METRE").toUpperCase();
      if (pref === "MILLI") return "MILLIMETRE";
      if (pref === "CENTI") return "CENTIMETRE";
      if (pref === "DECI") return "DECIMETRE";
      return name;
    }
    const si = text.match(
      /IFCSIUNIT\s*\([^,]*,\s*\.LENGTHUNIT\.\s*,\s*\$\s*,\s*\.([A-Z_]+)\./i,
    );
    if (si?.[1]) return si[1];
  } catch (err) {
    console.warn("detectLengthUnitFromIfcText", err);
  }
  return "METRE";
}

type ParsedGridAxis = {
  tag: string;
  /** IFC Z-up coordinates in file length units. */
  points: [number, number, number][];
};

/**
 * Compact IFC text parse for IFCGRIDAXIS polylines (+ optional grid placement).
 * Handles multi-line entities via paren-depth scan. Never throws.
 */
function parseIfcGridAxesFromText(text: string): ParsedGridAxis[] {
  const axes: ParsedGridAxis[] = [];
  try {
    type Ent = { type: string; body: string };
    const byId = new Map<number, Ent>();
    const n = text.length;
    let i = 0;
    while (i < n) {
      if (text.charCodeAt(i) !== 35 /* # */) {
        i++;
        continue;
      }
      let j = i + 1;
      while (j < n) {
        const c = text.charCodeAt(j);
        if (c < 48 || c > 57) break;
        j++;
      }
      if (j === i + 1) {
        i++;
        continue;
      }
      const id = Number(text.slice(i + 1, j));
      while (j < n && (text[j] === " " || text[j] === "\t")) j++;
      if (text[j] !== "=") {
        i++;
        continue;
      }
      j++;
      while (j < n && (text[j] === " " || text[j] === "\t" || text[j] === "\r" || text[j] === "\n"))
        j++;
      const typeStart = j;
      while (j < n) {
        const c = text.charCodeAt(j);
        if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 48 && c <= 57)) {
          j++;
          continue;
        }
        break;
      }
      const type = text.slice(typeStart, j).toUpperCase();
      while (j < n && (text[j] === " " || text[j] === "\t" || text[j] === "\r" || text[j] === "\n"))
        j++;
      if (text[j] !== "(") {
        i = j;
        continue;
      }
      let depth = 0;
      const bodyStart = j;
      for (; j < n; j++) {
        const ch = text[j];
        if (ch === "'") {
          j++;
          while (j < n && text[j] !== "'") {
            if (text[j] === "\\") j++;
            j++;
          }
          continue;
        }
        if (ch === "(") depth++;
        else if (ch === ")") {
          depth--;
          if (depth === 0) {
            j++;
            break;
          }
        }
      }
      const body = text.slice(bodyStart, j);
      if (
        type === "IFCGRIDAXIS" ||
        type === "IFCPOLYLINE" ||
        type === "IFCCARTESIANPOINT" ||
        type === "IFCGRID" ||
        type === "IFCLOCALPLACEMENT" ||
        type === "IFCAXIS2PLACEMENT3D"
      ) {
        byId.set(id, { type, body });
      }
      while (j < n && text[j] !== ";") j++;
      i = j + 1;
    }

    const parsePoint = (ent: Ent | undefined): [number, number, number] | null => {
      if (!ent || ent.type !== "IFCCARTESIANPOINT") return null;
      const nums = ent.body.match(/-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g);
      if (!nums || nums.length < 2) return null;
      const x = Number(nums[0]);
      const y = Number(nums[1]);
      const z = nums.length >= 3 ? Number(nums[2]) : 0;
      if (![x, y, z].every(Number.isFinite)) return null;
      return [x, y, z];
    };

    const resolvePlacementLoc = (
      refId: number | null,
      depth = 0,
    ): [number, number, number] => {
      if (refId == null || depth > 12) return [0, 0, 0];
      const ent = byId.get(refId);
      if (!ent) return [0, 0, 0];
      if (ent.type === "IFCLOCALPLACEMENT") {
        const refs = [...ent.body.matchAll(/#(\d+)/g)].map((m) => Number(m[1]));
        // body is "(PlacementRelTo, RelativePlacement)" — $ means no parent.
        const hasParent = /^\(\s*#/.test(ent.body);
        if (hasParent && refs.length >= 2) {
          const [px, py, pz] = resolvePlacementLoc(refs[0]!, depth + 1);
          const [rx, ry, rz] = resolvePlacementLoc(refs[1]!, depth + 1);
          return [px + rx, py + ry, pz + rz];
        }
        // Only RelativePlacement ref present
        return resolvePlacementLoc(refs[0] ?? null, depth + 1);
      }
      if (ent.type === "IFCAXIS2PLACEMENT3D") {
        const refs = [...ent.body.matchAll(/#(\d+)/g)].map((m) => Number(m[1]));
        const loc = refs[0];
        return parsePoint(byId.get(loc)) ?? [0, 0, 0];
      }
      if (ent.type === "IFCCARTESIANPOINT") {
        return parsePoint(ent) ?? [0, 0, 0];
      }
      return [0, 0, 0];
    };

    // Optional: world offset from first IFCGRID ObjectPlacement (6th attr ≈ 3rd # after GlobalId string…)
    let place: [number, number, number] = [0, 0, 0];
    for (const ent of byId.values()) {
      if (ent.type !== "IFCGRID") continue;
      // Attributes: GlobalId, OwnerHistory, Name, Description, ObjectType, ObjectPlacement, ...
      // Find ObjectPlacement: after two #refs (OwnerHistory + often none for strings), scan for 6th field.
      // Practical: take the #ref that is IFCLOCALPLACEMENT appearing early.
      const refs = [...ent.body.matchAll(/#(\d+)/g)].map((m) => Number(m[1]));
      for (const rid of refs) {
        const r = byId.get(rid);
        if (r?.type === "IFCLOCALPLACEMENT") {
          place = resolvePlacementLoc(rid);
          break;
        }
      }
      break;
    }

    for (const ent of byId.values()) {
      if (ent.type !== "IFCGRIDAXIS") continue;
      const tagMatch = ent.body.match(/^\(\s*'((?:[^']|'')*)'/);
      const tag = (tagMatch?.[1] ?? "").replace(/''/g, "'").trim();
      if (!tag) continue;
      const curveMatch = ent.body.match(/^\(\s*'(?:[^']|'')*'\s*,\s*#(\d+)/);
      if (!curveMatch) continue;
      const curveId = Number(curveMatch[1]);
      const curve = byId.get(curveId);
      if (!curve || curve.type !== "IFCPOLYLINE") continue;
      const pointIds = [...curve.body.matchAll(/#(\d+)/g)].map((m) => Number(m[1]));
      const points: [number, number, number][] = [];
      for (const pid of pointIds) {
        const pt = parsePoint(byId.get(pid));
        if (!pt) continue;
        points.push([
          pt[0] + place[0],
          pt[1] + place[1],
          pt[2] + place[2],
        ]);
      }
      if (points.length >= 2) axes.push({ tag, points });
    }
  } catch (err) {
    console.warn("parseIfcGridAxesFromText", err);
  }
  return axes;
}

/** Canvas sprite bubble with tag text (no FontLoader). */
function makeTagBubbleSprite(
  tag: string,
  radiusWorld: number,
  isMain: boolean,
): THREE.Sprite {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.clearRect(0, 0, size, size);
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 6, 0, Math.PI * 2);
    // High-contrast plan bubble fill
    ctx.fillStyle = isMain ? "#e8f2f8" : "#c8d8e4";
    ctx.fill();
    ctx.strokeStyle = "#0a1218";
    ctx.lineWidth = 10;
    ctx.stroke();
    ctx.fillStyle = "#0a1218";
    const fontPx = tag.length > 3 ? 30 : tag.length > 2 ? 38 : 50;
    ctx.font = `bold ${fontPx}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(tag, size / 2, size / 2 + 1);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  const mat = new THREE.SpriteMaterial({
    map: tex,
    // Depth-test so steel can occlude bubbles (not always on top).
    depthTest: true,
    depthWrite: false,
    transparent: true,
    opacity: isMain ? 0.96 : 0.8,
    sizeAttenuation: true,
  });
  const sprite = new THREE.Sprite(mat);
  // Compact plan bubbles — radiusWorld is already clamped small.
  const d = Math.max(radiusWorld * 2, 0.35);
  sprite.scale.set(d, d, 1);
  sprite.renderOrder = 3;
  sprite.userData.eccOwned = true;
  sprite.userData.eccDisposeMaterial = true;
  sprite.userData.kind = "label";
  sprite.userData.tag = tag;
  sprite.frustumCulled = false;
  return sprite;
}

/**
 * Canvas plan bubbles at both ends of tagged axis lines.
 * Default radius from root Box3: clamp(maxDim * 0.0045, 0.28, 0.85).
 * Skips lines already marked eccBubblesDone. Never throws.
 */
function addCanvasBubblesForAxisLines(
  root: THREE.Object3D,
  worldRadius?: number,
): void {
  try {
    let baseRadius = worldRadius;
    if (baseRadius == null || !(baseRadius > 0)) {
      const box = new THREE.Box3().setFromObject(root);
      if (!box.isEmpty()) {
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z, 1);
        baseRadius = THREE.MathUtils.clamp(maxDim * 0.0045, 0.28, 0.85);
      } else {
        baseRadius = 1.8;
      }
    }
    const tmpA = new THREE.Vector3();
    const tmpB = new THREE.Vector3();
    root.traverse((obj) => {
      try {
        const isLineKind = obj.userData.kind === "line";
        const isLineObj =
          (obj as THREE.Line).isLine === true && !!obj.userData.tag;
        if (!isLineKind && !isLineObj) return;
        if (obj.userData.eccBubblesDone) return;
        const tag = String(obj.userData.tag ?? "").trim();
        if (!tag) return;
        const line = obj as THREE.Line;
        const geo = line.geometry;
        const pos = geo?.getAttribute?.("position") as
          | THREE.BufferAttribute
          | undefined;
        if (!pos || pos.count < 2) return;
        tmpA.fromBufferAttribute(pos, 0);
        tmpB.fromBufferAttribute(pos, pos.count - 1);
        const isMain = isMainGridAxisTag(tag);
        const radius = isMain ? baseRadius! * 1.12 : baseRadius! * 0.88;
        const yLift = Math.max(baseRadius! * 0.04, 0.08);
        for (const end of [tmpA, tmpB]) {
          const spr = makeTagBubbleSprite(tag, radius, isMain);
          spr.position.set(end.x, end.y + yLift, end.z);
          line.add(spr);
        }
        obj.userData.eccBubblesDone = true;
      } catch (err) {
        console.warn("addCanvasBubblesForAxisLines line", err);
      }
    });
  } catch (err) {
    console.warn("addCanvasBubblesForAxisLines", err);
  }
}

/**
 * Build fallback grid group from parsed IFC axes.
 * Scale file units → meters, Z-up→Y-up, then align to model box
 * (COORDINATE_TO_ORIGIN friendly — no second unit scale).
 */
function buildEccIfcGridGroup(
  axes: ParsedGridAxis[],
  unitToM: number,
  modelObject: THREE.Object3D | null,
): THREE.Group {
  const group = new THREE.Group();
  group.name = "ecc-ifc-fallback-grids";
  const mainMat = new THREE.LineBasicMaterial({
    color: ECC_GRID_MAIN_COLOR,
    transparent: true,
    opacity: 0.95,
    // Depth-test so steel can occlude grid lines (not always on top).
    depthTest: true,
    depthWrite: false,
  });
  mainMat.userData = { eccDisposeMaterial: true };
  const secMat = new THREE.LineBasicMaterial({
    color: ECC_GRID_SEC_COLOR,
    transparent: true,
    opacity: 0.45,
    depthTest: true,
    depthWrite: false,
  });
  // Mark mats via a dummy flag on first consumer objects instead:
  let axisCount = 0;
  for (const axis of axes) {
    if (axis.points.length < 2) continue;
    const isMain = isMainGridAxisTag(axis.tag);
    // IFC Z-up (x,y,z) → three Y-up (x,z,y); scale once by length unit.
    const pts = axis.points.map(
      ([x, y, z]) =>
        new THREE.Vector3(x * unitToM, z * unitToM, y * unitToM),
    );
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const line = new THREE.Line(geo, isMain ? mainMat : secMat);
    line.renderOrder = 2;
    line.frustumCulled = false;
    line.userData.tag = axis.tag;
    line.userData.kind = "line";
    line.userData.eccOwned = true;
    line.userData.eccDisposeGeometry = true;
    if (!isMain) line.userData.eccDisposeMaterial = true;
    group.add(line);
    axisCount++;
  }
  // Ensure shared mainMat gets disposed with group (attach flag to group)
  group.userData.eccSharedMats = [mainMat, secMat];

  // Radius from pre-align grid extent (meters) so bubbles scale with building.
  let bubbleRadius: number | undefined;
  try {
    const preBox = new THREE.Box3().setFromObject(group);
    if (!preBox.isEmpty()) {
      const size = new THREE.Vector3();
      preBox.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z, 1);
      bubbleRadius = THREE.MathUtils.clamp(maxDim * 0.0045, 0.28, 0.85);
    }
  } catch {
    /* ignore */
  }

  if (modelObject && axisCount > 0) {
    try {
      const gridBox = new THREE.Box3().setFromObject(group);
      const modelBox = new THREE.Box3().setFromObject(modelObject);
      if (!gridBox.isEmpty() && !modelBox.isEmpty()) {
        const gc = new THREE.Vector3();
        const mc = new THREE.Vector3();
        gridBox.getCenter(gc);
        modelBox.getCenter(mc);
        group.position.x += mc.x - gc.x;
        group.position.y += modelBox.min.y - gridBox.min.y;
        group.position.z += mc.z - gc.z;
      }
    } catch (err) {
      console.warn("buildEccIfcGridGroup align", err);
    }
  }

  if (axisCount > 0) {
    addCanvasBubblesForAxisLines(group, bubbleRadius);
  }
  console.info(`ECC IFC fallback grids: ${axisCount} axis line(s)`);
  return group;
}

type PieceMark = {
  mark: string;
  modelId: string;
  localId: number;
};

type MemberNode = PieceMark & {
  /** Direct children; null until first + expand loads them. */
  children: MemberNode[] | null;
  hasChildren: boolean;
};

type HarvestedMark = PieceMark & { category: string };

function memberKey(m: Pick<PieceMark, "modelId" | "localId">): string {
  return `${m.modelId}:${m.localId}`;
}

const CHILD_REL_KEYS = ["IsDecomposedBy", "HasOpenings", "IsNestedBy", "HasCoverings"] as const;

const SPATIAL_CATS = new Set([
  "PROJECT",
  "SITE",
  "BUILDING",
  "BUILDINGSTOREY",
  "SPACE",
  "EXTERNALSPATIALELEMENT",
  "SPATIALZONE",
  "SPATIALELEMENT",
]);

const MEMBER_LIKE_CATS = new Set([
  "ELEMENTASSEMBLY",
  "MEMBER",
  "BEAM",
  "COLUMN",
  "BUILDINGELEMENTPROXY",
]);

const el = {
  viewport: document.getElementById("viewport") as HTMLDivElement,
  status: document.getElementById("status") as HTMLDivElement,
  welcome: document.getElementById("welcome") as HTMLDivElement,
  propsPanel: document.getElementById("props-panel") as HTMLElement,
  propsBody: document.getElementById("props-body") as HTMLDivElement,
  propsActions: document.getElementById("props-actions") as HTMLDivElement,
  toolsPanel: document.getElementById("tools-panel") as HTMLElement,
  marksList: document.getElementById("marks-list") as HTMLDivElement,
  membersSection: document.getElementById("members-section") as HTMLElement,
  membersToggle: document.getElementById("members-toggle") as HTMLButtonElement,
  membersBody: document.getElementById("members-body") as HTMLDivElement,
  marksSearch: document.getElementById("marks-search") as HTMLInputElement,
  welcomeFile: document.getElementById("welcome-file") as HTMLInputElement,
  btnFit: document.getElementById("btn-fit") as HTMLButtonElement,
  btnClearHl: document.getElementById("btn-clear-hl") as HTMLButtonElement,
  btnShowAll: document.getElementById("btn-show-all") as HTMLButtonElement,
  btnIsolate: document.getElementById("btn-isolate") as HTMLButtonElement,
  btnIsolateSel: document.getElementById("btn-isolate-sel") as HTMLButtonElement,
  btnPropsClose: document.getElementById("btn-props-close") as HTMLButtonElement,
  btnLegal: document.getElementById("btn-legal") as HTMLButtonElement,
  legalPanel: document.getElementById("legal-panel") as HTMLElement,
  btnLegalClose: document.getElementById("btn-legal-close") as HTMLButtonElement,
};

function setStatus(msg: string, kind: "" | "error" | "busy" = "") {
  el.status.textContent = msg;
  el.status.classList.toggle("error", kind === "error");
  el.status.classList.toggle("busy", kind === "busy");
  el.status.hidden = !msg;
}

/** Basename-only sanitize; reject path traversal / odd names. */
function sanitizeIfcFilename(raw: string | null): string | null {
  if (!raw) return null;
  const base = raw.replace(/\\/g, "/").split("/").pop() ?? "";
  if (!IFC_NAME_RE.test(base)) return null;
  if (base.includes("..")) return null;
  return base;
}

function getQueryFilename(): string | null {
  const params = new URLSearchParams(window.location.search);
  return sanitizeIfcFilename(params.get("f"));
}

function showWelcome(show: boolean) {
  el.welcome.hidden = !show;
}

function showProps(show: boolean) {
  el.propsPanel.hidden = !show;
}

function showTools(show: boolean) {
  el.toolsPanel.hidden = !show;
}

function setHlButtons(enabled: boolean) {
  el.btnClearHl.disabled = !enabled;
  el.btnShowAll.disabled = !enabled;
  el.btnIsolate.disabled = !enabled;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function valueToString(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object" && v !== null && "value" in (v as object)) {
    return String((v as { value: unknown }).value ?? "");
  }
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function stripIfcPrefix(raw: string): string {
  return raw.replace(/^IFC/i, "").trim();
}

function titleCase(s: string): string {
  return s
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function friendlyCategory(raw: string): string {
  const key = stripIfcPrefix(raw).toUpperCase();
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key];
  const base = titleCase(stripIfcPrefix(raw));
  if (!base) return raw;
  return /s$/i.test(base) ? base : `${base}s`;
}

function friendlyTypeValue(raw: string): string {
  const t = raw.trim();
  if (!t || /^NOTDEFINED$/i.test(t) || /^USERDEFINED$/i.test(t)) return "";
  if (/^IFC[A-Z0-9_]+$/i.test(t)) return titleCase(stripIfcPrefix(t));
  return titleCase(t);
}

function isJunkKey(key: string): boolean {
  if (!key || key.startsWith("_")) return true;
  if (HIDDEN_ATTR_KEYS.has(key.toLowerCase())) return true;
  return false;
}

function isBareIfcType(val: string): boolean {
  return /^IFC[A-Z][A-Z0-9_]*$/i.test(val.trim());
}

function lookLikeMarkKey(key: string): boolean {
  const k = key.toLowerCase().replace(/\s+/g, " ").trim();
  if (MARK_KEYS.has(k)) return true;
  if (/(^|_| )(piece|assembly|member|part)[ _]?mark$/i.test(k)) return true;
  if (k === "mark" || k === "tag") return true;
  return false;
}

function extractLocalId(item: unknown): number | null {
  if (!item || typeof item !== "object") return null;
  const data = item as Record<string, unknown>;
  const raw = data["_localId"] ?? data["localId"];
  const n = Number(valueToString(raw));
  return Number.isFinite(n) ? n : null;
}

function categoryKey(raw: string): string {
  return stripIfcPrefix(raw).toUpperCase();
}

function isSpatialCategory(raw: string): boolean {
  return !!raw && SPATIAL_CATS.has(categoryKey(raw));
}

function isMemberLikeCategory(raw: string): boolean {
  return !!raw && MEMBER_LIKE_CATS.has(categoryKey(raw));
}

function relationEntries(item: Record<string, unknown>, key: string): Record<string, unknown>[] {
  const arr = item[key];
  if (!Array.isArray(arr)) return [];
  const out: Record<string, unknown>[] = [];
  for (const child of arr) {
    if (child && typeof child === "object") out.push(child as Record<string, unknown>);
  }
  return out;
}

function collectRelatedIds(item: Record<string, unknown>): number[] {
  const ids: number[] = [];
  for (const rel of CHILD_REL_KEYS) {
    for (const child of relationEntries(item, rel)) {
      const id = extractLocalId(child);
      if (id != null) ids.push(id);
    }
  }
  return ids;
}

/** True if this item is aggregated into a non-spatial parent (geometry/assembly). */
function isAggregatedChild(item: Record<string, unknown>, geometryIds: Set<number>): boolean {
  const parents = relationEntries(item, "Decomposes");
  if (!parents.length) return false;
  let sawParent = false;
  for (const parent of parents) {
    const pid = extractLocalId(parent);
    const cat = valueToString(parent["_category"] ?? parent["type"] ?? "");
    if (cat && isSpatialCategory(cat)) continue;
    sawParent = true;
    if (pid != null && geometryIds.has(pid)) return true;
    if (cat && !isSpatialCategory(cat)) return true;
    if (pid != null) return true;
  }
  return sawParent;
}

function firstMarkCandidate(item: Record<string, unknown>): string {
  for (const key of ["Tag", "Mark"]) {
    const v = valueToString(item[key]).trim();
    if (v && !/^IFC/i.test(v) && v.length <= 64) return v;
  }
  const psets = item["IsDefinedBy"] ?? item["HasPropertySets"];
  if (Array.isArray(psets)) {
    for (const pset of psets) {
      if (!pset || typeof pset !== "object") continue;
      const ps = pset as Record<string, unknown>;
      const props = ps["HasProperties"] ?? ps["properties"];
      if (Array.isArray(props)) {
        for (const prop of props) {
          if (!prop || typeof prop !== "object") continue;
          const pr = prop as Record<string, unknown>;
          const n = valueToString(pr["Name"] ?? pr["name"]);
          if (!lookLikeMarkKey(n)) continue;
          const v = valueToString(
            pr["NominalValue"] ?? pr["value"] ?? pr["Value"] ?? "",
          ).trim();
          if (v && !/^IFC/i.test(v) && v.length <= 64) return v;
        }
      }
    }
  }
  const name = valueToString(item["Name"]).trim();
  if (name && !/^IFC/i.test(name) && name.length <= 64) return name;
  return "";
}

function labelForItem(item: Record<string, unknown>, fallbackLocalId: number): string {
  return firstMarkCandidate(item) || `#${fallbackLocalId}`;
}

function renderProperties(items: unknown[]) {
  if (!items.length) {
    el.propsBody.innerHTML = `<p class="muted">Click an item to inspect.</p>`;
    el.propsActions.hidden = false;
    showProps(true);
    return;
  }

  const parts: string[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const data = item as Record<string, unknown>;

    const rows: Array<[string, string]> = [];
    const objectType = friendlyTypeValue(valueToString(data["ObjectType"]));
    const predefined = friendlyTypeValue(valueToString(data["PredefinedType"]));
    const categoryRaw = valueToString(data["_category"]);
    const typeVal = objectType || predefined || friendlyTypeValue(categoryRaw);
    const tag = valueToString(data["Tag"]).trim();
    const desc = valueToString(data["Description"]).trim();

    if (typeVal) rows.push(["Type", typeVal]);
    if (tag) rows.push(["Mark", tag]);
    if (desc) rows.push(["Description", desc]);

    if (!rows.length) {
      parts.push(`<p class="muted">No Type / Mark / Description for this item.</p>`);
      continue;
    }
    parts.push(`<dl>`);
    for (const [k, v] of rows) {
      parts.push(`<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`);
    }
    parts.push(`</dl>`);
  }

  el.propsBody.innerHTML =
    parts.join("") || `<p class="muted">No Type / Mark / Description for this item.</p>`;
  el.propsActions.hidden = false;
  showProps(true);
}

/** Simple fingerprint: filename + byteLength + first/last 4k hex prefix. */
async function cacheKeyForIfc(filename: string, buffer: Uint8Array): Promise<string> {
  const n = buffer.byteLength;
  const head = buffer.subarray(0, Math.min(4096, n));
  const tail = n > 4096 ? buffer.subarray(n - 4096) : head;
  const concat = new Uint8Array(head.byteLength + tail.byteLength);
  concat.set(head, 0);
  concat.set(tail, head.byteLength);
  let hex = "";
  try {
    const digest = await crypto.subtle.digest("SHA-256", concat);
    const bytes = new Uint8Array(digest);
    for (let i = 0; i < 8; i++) hex += bytes[i]!.toString(16).padStart(2, "0");
  } catch {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < concat.length; i++) {
      h ^= concat[i]!;
      h = Math.imul(h, 16777619) >>> 0;
    }
    hex = h.toString(16);
  }
  return `${filename}|${n}|${hex}`;
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

async function idbGet(key: string): Promise<ArrayBuffer | null> {
  try {
    const db = await openIdb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => {
        const v = req.result;
        if (v instanceof ArrayBuffer) resolve(v);
        else if (v instanceof Uint8Array) {
          resolve(
            v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength) as ArrayBuffer,
          );
        } else if (v?.buffer instanceof ArrayBuffer) {
          resolve(v.buffer);
        } else resolve(null);
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn("idb get", err);
    return null;
  }
}

async function idbPut(key: string, data: ArrayBuffer): Promise<void> {
  try {
    const db = await openIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("idb put", err);
  }
}

function modelIdMapSize(map: OBC.ModelIdMap): number {
  let n = 0;
  for (const ids of Object.values(map)) n += ids.size;
  return n;
}

function cloneModelIdMap(map: OBC.ModelIdMap): OBC.ModelIdMap {
  const out: OBC.ModelIdMap = {};
  for (const [k, v] of Object.entries(map)) out[k] = new Set(v);
  return out;
}

async function main() {
  const components = new OBC.Components();
  const worlds = components.get(OBC.Worlds);

  const world = worlds.create<
    OBC.SimpleScene,
    OBC.OrthoPerspectiveCamera,
    OBC.SimpleRenderer
  >();

  world.scene = new OBC.SimpleScene(components);
  world.scene.setup();
  world.scene.three.background = new THREE.Color(0x0f1620);

  world.renderer = new OBC.SimpleRenderer(components, el.viewport);
  world.renderer.showLogo = false;
  world.camera = new OBC.OrthoPerspectiveCamera(components);
  await world.camera.controls.setLookAt(12, 8, 12, 0, 0, 0);

  components.init();

  // iOS Safari address-bar show/hide does not always fire window.resize / RO alone.
  const requestCanvasResize = () => {
    try {
      world.renderer?.resize();
    } catch {
      /* ignore */
    }
  };
  window.visualViewport?.addEventListener("resize", requestCanvasResize);
  window.visualViewport?.addEventListener("scroll", requestCanvasResize);

  const amb = new THREE.AmbientLight(0xb8c8d8, 0.85);
  const dir = new THREE.DirectionalLight(0xffffff, 1.05);
  dir.position.set(8, 16, 6);
  world.scene.three.add(amb, dir);

  // Same-origin worker (shipped as public/fragments-worker.js = That Open worker.min.mjs).
  // Avoids unpkg on mobile networks; module worker URL must be same-origin.
  const fragments = components.get(OBC.FragmentsManager);
  const localWorker = new URL(
    `${import.meta.env.BASE_URL}fragments-worker.js`,
    window.location.href,
  ).href;
  try {
    fragments.init(localWorker);
  } catch (err) {
    console.warn("local fragments worker init failed, falling back to unpkg", err);
    fragments.init(await OBC.FragmentsManager.getWorker());
  }

  world.camera.controls.addEventListener("update", () => {
    fragments.core.update();
  });

  fragments.list.onItemSet.add(({ value: model }) => {
    model.useCamera(world.camera.three);
    world.scene.three.add(model.object);
    fragments.core.update(true);
  });

  // Fragments built-in IFC grids (same transform/units as converted mesh).
  const eccModelGridGroups: THREE.Object3D[] = [];
  /** Raw IFC bytes kept for grid fallback when getGrids hangs. */
  let pendingIfcBytes: Uint8Array | null = null;
  let fileLengthUnit = "METRE";

  const clearEccModelGrids = () => {
    try {
      while (eccModelGridGroups.length) {
        const g = eccModelGridGroups.pop()!;
        disposeEccGrid(g, world.scene.three);
      }
    } catch (err) {
      console.warn("clearEccModelGrids", err);
    }
  };

  /** Fallback grids from raw IFC text when Fragments getGrids hangs/fails. */
  function showEccFallbackIfcGrids(): void {
    try {
      if (!pendingIfcBytes || pendingIfcBytes.byteLength < 32) {
        console.warn("showEccFallbackIfcGrids: no pending IFC bytes");
        return;
      }
      const text = new TextDecoder("utf-8", { fatal: false }).decode(
        pendingIfcBytes,
      );
      const unitName = fileLengthUnit || detectLengthUnitFromIfcText(text);
      fileLengthUnit = unitName;
      const unitToM = lengthUnitToMeters(unitName);
      const axes = parseIfcGridAxesFromText(text);
      if (!axes.length) {
        console.warn("showEccFallbackIfcGrids: no IFCGRIDAXIS polylines");
        return;
      }
      let modelObj: THREE.Object3D | null = null;
      for (const [, model] of fragments.list) {
        if (model.object) {
          modelObj = model.object;
          break;
        }
      }
      const group = buildEccIfcGridGroup(axes, unitToM, modelObj);
      world.scene.three.add(group);
      eccModelGridGroups.push(group);
    } catch (err) {
      console.warn("showEccFallbackIfcGrids", err);
    }
  }

  /**
   * Show IFC grids via Fragments model.getGrids (correct origin/units/scale).
   * Hard-timeout on worker invoke; fallback to raw IFC parse. Never throws.
   */
  async function showEccModelGrids(): Promise<void> {
    try {
      clearEccModelGrids();
      // Canvas plan bubbles only — skip FontLoader / ShapeGeometry labels.
      let axisCount = 0;
      let gotAny = false;
      for (const [, model] of fragments.list) {
        try {
          const lineMat = new THREE.LineBasicMaterial({
            color: ECC_GRID_MAIN_COLOR,
            transparent: true,
            opacity: 0.95,
            // Depth-test so steel can occlude grid lines (not always on top).
            depthTest: true,
            depthWrite: false,
          });
          model.setGridMaterial(lineMat);

          const gridsPromise = model.getGrids({
            labels: { show: false },
          });
          const grids = await withTimeout(gridsPromise, 6000, "model.getGrids");
          if (!grids) {
            console.warn(
              "showEccModelGrids: getGrids timed out/failed — will try IFC fallback",
            );
            continue;
          }

          const secMat = lineMat.clone();
          secMat.color.setHex(ECC_GRID_SEC_COLOR);
          secMat.opacity = 0.45;
          secMat.transparent = true;
          secMat.depthTest = true;
          secMat.depthWrite = false;

          const nativeLabels: THREE.Object3D[] = [];
          grids.traverse((obj) => {
            const tag = String(obj.userData.tag ?? "").trim();
            const kind = obj.userData.kind;
            if (kind === "line") {
              axisCount++;
              const line = obj as THREE.Line;
              if (tag && !isMainGridAxisTag(tag)) {
                line.material = secMat;
              }
              line.renderOrder = 2;
            }
            if (kind === "label") {
              // Prefer canvas sprites only — hide/remove native text labels.
              nativeLabels.push(obj);
            }
          });
          for (const lab of nativeLabels) {
            try {
              lab.visible = false;
              lab.removeFromParent();
            } catch {
              /* ignore */
            }
          }

          // Building-scale canvas bubbles (even if native labels were present).
          let modelRadius: number | undefined;
          try {
            const mob = model.object;
            if (mob) {
              const mb = new THREE.Box3().setFromObject(mob);
              if (!mb.isEmpty()) {
                const sz = new THREE.Vector3();
                mb.getSize(sz);
                const maxDim = Math.max(sz.x, sz.y, sz.z, 1);
                modelRadius = THREE.MathUtils.clamp(maxDim * 0.0045, 0.28, 0.85);
              }
            }
          } catch {
            /* ignore */
          }
          addCanvasBubblesForAxisLines(grids, modelRadius);

          grids.name = "ecc-fragments-grids";
          world.scene.three.add(grids);
          eccModelGridGroups.push(grids);
          gotAny = true;
        } catch (err) {
          console.warn("showEccModelGrids model", err);
        }
      }
      if (!gotAny || axisCount === 0) {
        if (gotAny && axisCount === 0) {
          clearEccModelGrids();
        }
        showEccFallbackIfcGrids();
      } else {
        console.info(
          `ECC Fragments grids: ${eccModelGridGroups.length} group(s), ${axisCount} axis line(s) (canvas bubbles)`,
        );
      }
    } catch (err) {
      console.warn("showEccModelGrids", err);
      try {
        showEccFallbackIfcGrids();
      } catch (fbErr) {
        console.warn("showEccFallbackIfcGrids after error", fbErr);
      }
    }
  }

  fragments.core.models.materials.list.onItemSet.add(({ value: material }) => {
    if (!("isLodMaterial" in material && (material as { isLodMaterial?: boolean }).isLodMaterial)) {
      material.polygonOffset = true;
      material.polygonOffsetUnits = 1;
      material.polygonOffsetFactor = Math.random();
    }
  });

  const ifcLoader = components.get(OBC.IfcLoader);
  // Same-origin WASM under public/wasm/ (also avoids COEP/CDN issues on phones).
  const localWasm = new URL(`${import.meta.env.BASE_URL}wasm/`, window.location.href).href;
  await ifcLoader.setup({
    autoSetWasm: false,
    wasm: {
      path: localWasm,
      absolute: true,
    },
  });

  components.get(OBC.Raycasters).get(world);
  const highlighter = components.get(OBF.Highlighter);
  highlighter.setup({
    world,
    selectMaterialDefinition: {
      color: new THREE.Color("#00c2d4"),
      renderedFaces: FRAGS.RenderedFaces.ONE,
      opacity: 1,
      transparent: false,
      preserveOriginalMaterial: true,
      _explicitProps: ["color", "opacity", "transparent"],
    },
  });

  function hlMaterial(hex: string, priority: number) {
    return {
      color: new THREE.Color(hex),
      renderedFaces: FRAGS.RenderedFaces.ONE,
      opacity: 1,
      transparent: false,
      preserveOriginalMaterial: true,
      _explicitProps: ["color", "opacity", "transparent"],
      priority,
    };
  }

  for (let i = 0; i < HL_PALETTE.length; i++) {
    highlighter.styles.set(`${ECC_HL_PREFIX}${i}`, hlMaterial(HL_PALETTE[i]!, 5));
  }
  // Stronger / brighter for assembly root (or clicked climb target)
  highlighter.styles.set(ECC_HL_ROOT, hlMaterial("#ffffff", 8));

  const classifier = components.get(OBC.Classifier);
  const hider = components.get(OBC.Hider);

  let loadGen = 0;
  let activeHighlight: OBC.ModelIdMap = {};
  let lastSelection: OBC.ModelIdMap = {};
  let pieceMarks: MemberNode[] = [];
  let activeCategory: string | null = null;
  let activeMark: string | null = null;
  let activeMemberKey: string | null = null;
  /** Assembly root currently selected (modelId:localId) — drives two-step props. */
  let activeRootKey: string | null = null;
  const expandedMemberKeys = new Set<string>();
  let marksQuery = "";
  /** localIds known to have IsDecomposedBy / children during harvest */
  let knownParentIds = new Set<number>();

  function setActiveHighlight(map: OBC.ModelIdMap) {
    activeHighlight = cloneModelIdMap(map);
    setHlButtons(modelIdMapSize(activeHighlight) > 0 || fragments.list.size > 0);
  }

  function eccHlStyleNames(): string[] {
    const names = HL_PALETTE.map((_, i) => `${ECC_HL_PREFIX}${i}`);
    names.push(ECC_HL_ROOT);
    names.push("ecc"); // legacy single-color style, if any linger
    return names;
  }

  async function clearEccHighlights() {
    for (const name of eccHlStyleNames()) {
      try {
        await highlighter.clear(name);
      } catch {
        /* ignore */
      }
    }
  }

  /** Paint each localId a distinct palette color (stable sort by id). */
  async function applyAssemblyColors(
    map: OBC.ModelIdMap,
    opts?: { zoom?: boolean; rootMap?: OBC.ModelIdMap },
  ) {
    await clearEccHighlights();
    if (modelIdMapSize(map) === 0) return;

    const rootKeys = new Set<string>();
    if (opts?.rootMap) {
      for (const [modelId, ids] of Object.entries(opts.rootMap)) {
        for (const id of ids) rootKeys.add(`${modelId}:${id}`);
      }
    }

    type Entry = { modelId: string; localId: number };
    const entries: Entry[] = [];
    for (const [modelId, ids] of Object.entries(map)) {
      for (const id of ids) entries.push({ modelId, localId: id });
    }
    entries.sort(
      (a, b) =>
        a.localId - b.localId || a.modelId.localeCompare(b.modelId),
    );

    const byStyle = new Map<string, OBC.ModelIdMap>();
    const addTo = (style: string, modelId: string, localId: number) => {
      let m = byStyle.get(style);
      if (!m) {
        m = {};
        byStyle.set(style, m);
      }
      let set = m[modelId];
      if (!set) {
        set = new Set<number>();
        m[modelId] = set;
      }
      set.add(localId);
    };

    let paletteIdx = 0;
    for (const e of entries) {
      const key = `${e.modelId}:${e.localId}`;
      if (rootKeys.has(key)) {
        addTo(ECC_HL_ROOT, e.modelId, e.localId);
      } else {
        addTo(
          `${ECC_HL_PREFIX}${paletteIdx % HL_PALETTE.length}`,
          e.modelId,
          e.localId,
        );
        paletteIdx++;
      }
    }

    let zoomLeft = !!opts?.zoom;
    for (const [styleName, styleMap] of byStyle) {
      await highlighter.highlightByID(styleName, styleMap, false, zoomLeft);
      zoomLeft = false;
    }
  }

  async function applyHighlight(
    map: OBC.ModelIdMap,
    opts?: { clearSelect?: boolean; zoom?: boolean; rootMap?: OBC.ModelIdMap },
  ) {
    if (opts?.clearSelect !== false) {
      try {
        await highlighter.clear("select");
      } catch {
        /* ignore */
      }
    }
    if (modelIdMapSize(map) === 0) {
      await clearEccHighlights();
      setActiveHighlight({});
      return;
    }
    await applyAssemblyColors(map, { zoom: opts?.zoom, rootMap: opts?.rootMap });
    setActiveHighlight(map);
  }

  async function clearHighlights() {
    try {
      await highlighter.clear();
    } catch {
      /* ignore */
    }
    await clearEccHighlights();
    activeHighlight = {};
    activeCategory = null;
    activeMark = null;
    activeMemberKey = null;
    activeRootKey = null;
    document
      .querySelectorAll(".tools-item.active")
      .forEach((n) => n.classList.remove("active"));
    setHlButtons(fragments.list.size > 0);
  }

  async function showAll() {
    await hider.set(true);
    try {
      await showIfcFileGrids();
    } catch (err) {
      console.warn("showIfcFileGrids", err);
    }
    void showEccModelGrids().catch((err) =>
      console.warn("showEccModelGrids", err),
    );
    setStatus("");
  }

  async function isolateMap(map: OBC.ModelIdMap) {
    if (modelIdMapSize(map) === 0) {
      return;
    }
    await hider.isolate(map);
    setStatus("");
  }

  async function expandRelated(modelIdMap: OBC.ModelIdMap): Promise<OBC.ModelIdMap> {
    const out = cloneModelIdMap(modelIdMap);
    const MAX_ITERS = 20;
    const BATCH = 200;
    const relCfg = {
      IsDecomposedBy: { attributes: false, relations: false },
      HasOpenings: { attributes: false, relations: false },
      IsNestedBy: { attributes: false, relations: false },
      HasCoverings: { attributes: false, relations: false },
    };
    for (const [modelId, localIds] of Object.entries(modelIdMap)) {
      const model = fragments.list.get(modelId);
      if (!model) continue;
      const set = out[modelId] ?? new Set<number>();
      out[modelId] = set;
      let frontier = [...localIds];
      for (let iter = 0; iter < MAX_ITERS && frontier.length; iter++) {
        const next: number[] = [];
        const addNew = (id: number) => {
          if (!set.has(id)) {
            set.add(id);
            next.push(id);
          }
        };
        for (let i = 0; i < frontier.length; i += BATCH) {
          const slice = frontier.slice(i, i + BATCH);
          try {
            const kids = await model.getItemsChildren(slice);
            for (const id of kids) addNew(id);
          } catch (err) {
            console.warn("getItemsChildren", err);
          }
          try {
            const data = await model.getItemsData(slice, {
              attributesDefault: false,
              relations: relCfg,
            });
            for (const item of data) {
              if (!item || typeof item !== "object") continue;
              for (const id of collectRelatedIds(item as Record<string, unknown>)) {
                addNew(id);
              }
            }
          } catch (err) {
            console.warn("related relations", err);
          }
        }
        frontier = next;
      }
    }
    return out;
  }

  /** Walk Decomposes (and Nests) up to non-spatial assembly root(s). */
  async function resolveAssemblyRootsDetailed(
    modelIdMap: OBC.ModelIdMap,
  ): Promise<{ roots: OBC.ModelIdMap; climbedTo: OBC.ModelIdMap }> {
    const outRoots: OBC.ModelIdMap = {};
    const outClimbed: OBC.ModelIdMap = {};
    const MAX_HOPS = 20;
    const parentRelCfg = {
      Decomposes: { attributes: true, relations: false },
      Nests: { attributes: true, relations: false },
    };

    for (const [modelId, localIds] of Object.entries(modelIdMap)) {
      const model = fragments.list.get(modelId);
      if (!model) continue;
      const roots = new Set<number>();
      const climbedTo = new Set<number>();

      for (const startId of localIds) {
        let currentId = startId;
        let hops = 0;
        let didClimb = false;
        while (hops < MAX_HOPS) {
          hops++;
          let parentId: number | null = null;
          try {
            const data = await model.getItemsData([currentId], {
              attributesDefault: false,
              relations: parentRelCfg,
            });
            const item = data[0];
            if (!item || typeof item !== "object") break;
            const rec = item as Record<string, unknown>;

            const tryParents = (key: string) => {
              for (const parent of relationEntries(rec, key)) {
                const pid = extractLocalId(parent);
                const cat = valueToString(parent["_category"] ?? parent["type"] ?? "");
                if (cat && isSpatialCategory(cat)) continue;
                if (pid != null && pid !== currentId) {
                  parentId = pid;
                  return;
                }
                if (cat && !isSpatialCategory(cat) && pid != null) {
                  parentId = pid;
                  return;
                }
              }
            };

            tryParents("Decomposes");
            if (parentId == null) tryParents("Nests");

            // Soft fallback: isAggregatedChild with empty geometry set still
            // detects non-spatial Decomposes parents via category / pid.
            if (parentId == null && isAggregatedChild(rec, new Set())) {
              for (const parent of relationEntries(rec, "Decomposes")) {
                const pid = extractLocalId(parent);
                const cat = valueToString(parent["_category"] ?? parent["type"] ?? "");
                if (cat && isSpatialCategory(cat)) continue;
                if (pid != null && pid !== currentId) {
                  parentId = pid;
                  break;
                }
              }
            }
          } catch (err) {
            console.warn("resolveAssemblyRoots", err);
            break;
          }

          if (parentId == null) break;
          currentId = parentId;
          didClimb = true;
        }
        roots.add(currentId);
        if (didClimb) climbedTo.add(currentId);
      }

      if (roots.size) outRoots[modelId] = roots;
      if (climbedTo.size) outClimbed[modelId] = climbedTo;
    }
    return { roots: outRoots, climbedTo: outClimbed };
  }

  async function resolveAssemblyRoots(modelIdMap: OBC.ModelIdMap): Promise<OBC.ModelIdMap> {
    const { roots } = await resolveAssemblyRootsDetailed(modelIdMap);
    return roots;
  }


  function setMembersCollapsed(collapsed: boolean) {
    el.membersSection.classList.toggle("collapsed", collapsed);
    el.membersBody.hidden = collapsed;
    el.membersToggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  function nodeMatchesQuery(node: MemberNode, q: string): boolean {
    if (!q) return true;
    if (node.mark.toLowerCase().includes(q)) return true;
    if (node.children) {
      return node.children.some((c) => nodeMatchesQuery(c, q));
    }
    return false;
  }

  function renderMemberNode(node: MemberNode, depth: number): string {
    const key = memberKey(node);
    const q = marksQuery.trim().toLowerCase();
    if (q && !nodeMatchesQuery(node, q)) return "";

    const expanded = expandedMemberKeys.has(key);
    const active = activeMemberKey === key ? " active" : "";
    const canExpand = node.hasChildren || (node.children != null && node.children.length > 0);
    const toggleSym = expanded ? "−" : "+";
    const toggleCls = canExpand ? "" : " is-empty";
    const toggleDisabled = canExpand ? "" : " disabled";
    const kidsHtml =
      expanded && node.children && node.children.length
        ? `<div class="mark-children">${node.children.map((c) => renderMemberNode(c, depth + 1)).join("")}</div>`
        : expanded && node.children && !node.children.length
          ? `<div class="mark-children"><p class="muted">No sub-members</p></div>`
          : "";

    return `<div class="mark-node" data-depth="${depth}">
      <div class="mark-row">
        <button type="button" class="mark-toggle${toggleCls}" data-expand="${escapeHtml(key)}" title="${canExpand ? (expanded ? "Collapse" : "Show sub-members") : "No sub-members"}"${toggleDisabled}>${canExpand ? toggleSym : "·"}</button>
        <button type="button" class="tools-item mark-label${active}" data-mark="${escapeHtml(node.mark)}" data-model="${escapeHtml(node.modelId)}" data-id="${node.localId}" title="${escapeHtml(node.mark)}"><span>${escapeHtml(node.mark)}</span></button>
      </div>
      ${kidsHtml}
    </div>`;
  }

  function renderMarks() {
    if (!pieceMarks.length) {
      el.marksList.innerHTML = `<p class="muted">No members in this model.</p>`;
      return;
    }
    const q = marksQuery.trim().toLowerCase();
    const html = pieceMarks.map((m) => renderMemberNode(m, 0)).join("");
    if (q && !html.trim()) {
      el.marksList.innerHTML = `<p class="muted">No members match “${escapeHtml(marksQuery.trim())}”.</p>`;
      return;
    }
    el.marksList.innerHTML = html;
  }

  function findMemberNode(
    nodes: MemberNode[],
    modelId: string,
    localId: number,
  ): MemberNode | null {
    for (const n of nodes) {
      if (n.modelId === modelId && n.localId === localId) return n;
      if (n.children) {
        const hit = findMemberNode(n.children, modelId, localId);
        if (hit) return hit;
      }
    }
    return null;
  }

  async function loadMemberChildren(node: MemberNode): Promise<MemberNode[]> {
    const model = fragments.list.get(node.modelId);
    if (!model) return [];
    const childIdSet = new Set<number>();
    try {
      const kids = await model.getItemsChildren([node.localId]);
      for (const id of kids) childIdSet.add(id);
    } catch (err) {
      console.warn("getItemsChildren load", err);
    }
    try {
      const data = await model.getItemsData([node.localId], {
        attributesDefault: false,
        relations: {
          IsDecomposedBy: { attributes: false, relations: false },
          IsNestedBy: { attributes: false, relations: false },
        },
      });
      for (const item of data) {
        if (!item || typeof item !== "object") continue;
        const rec = item as Record<string, unknown>;
        for (const rel of ["IsDecomposedBy", "IsNestedBy"] as const) {
          for (const c of relationEntries(rec, rel)) {
            const id = extractLocalId(c);
            if (id != null) childIdSet.add(id);
          }
        }
      }
    } catch (err) {
      console.warn("relation children load", err);
    }
    childIdSet.delete(node.localId);
    const ids = [...childIdSet];
    if (!ids.length) return [];

    const byId = new Map<number, MemberNode>();
    const BATCH = 200;
    for (let i = 0; i < ids.length; i += BATCH) {
      const slice = ids.slice(i, i + BATCH);
      try {
        const data = await model.getItemsData(slice, {
          attributesDefault: true,
          relations: {
            IsDefinedBy: { attributes: true, relations: true },
            IsDecomposedBy: { attributes: false, relations: false },
          },
        });
        for (let j = 0; j < data.length; j++) {
          const item = data[j];
          const id = extractLocalId(item) ?? slice[j]!;
          if (!item || typeof item !== "object") {
            byId.set(id, {
              mark: `#${id}`,
              modelId: node.modelId,
              localId: id,
              children: null,
              hasChildren: knownParentIds.has(id),
            });
            continue;
          }
          const rec = item as Record<string, unknown>;
          const mark = labelForItem(rec, id);
          const hasKids =
            relationEntries(rec, "IsDecomposedBy").length > 0 || knownParentIds.has(id);
          byId.set(id, {
            mark,
            modelId: node.modelId,
            localId: id,
            children: null,
            hasChildren: hasKids,
          });
        }
      } catch (err) {
        console.warn("child mark batch", err);
        for (const id of slice) {
          if (!byId.has(id)) {
            byId.set(id, {
              mark: `#${id}`,
              modelId: node.modelId,
              localId: id,
              children: null,
              hasChildren: knownParentIds.has(id),
            });
          }
        }
      }
    }

    // Fill any missing ids and lightly probe nested expand affordance
    for (const id of ids) {
      if (!byId.has(id)) {
        byId.set(id, {
          mark: `#${id}`,
          modelId: node.modelId,
          localId: id,
          children: null,
          hasChildren: knownParentIds.has(id),
        });
      }
    }
    const nodes = [...byId.values()];
    const probe = nodes.filter((n) => !n.hasChildren).slice(0, 40);
    await Promise.all(
      probe.map(async (n) => {
        try {
          const ck = await model.getItemsChildren([n.localId]);
          if (ck.length) n.hasChildren = true;
        } catch {
          /* ignore */
        }
      }),
    );

    nodes.sort((a, b) =>
      a.mark.localeCompare(b.mark, undefined, { numeric: true, sensitivity: "base" }),
    );
    return nodes;
  }

  async function toggleMemberExpand(modelId: string, localId: number) {
    const node = findMemberNode(pieceMarks, modelId, localId);
    if (!node || (!node.hasChildren && !(node.children && node.children.length))) return;
    const key = memberKey(node);
    if (expandedMemberKeys.has(key)) {
      expandedMemberKeys.delete(key);
      renderMarks();
      return;
    }
    if (node.children == null) {
      node.children = await loadMemberChildren(node);
      node.hasChildren = node.children.length > 0;
    }
    if (node.children.length) expandedMemberKeys.add(key);
    else expandedMemberKeys.delete(key);
    renderMarks();
  }

  async function classifyModel() {
    try {
      await classifier.byCategory();
    } catch (err) {
      console.warn("byCategory", err);
    }
    try {
      await classifier.byIfcBuildingStorey({ classificationName: "Storeys" });
    } catch (err) {
      console.warn("byIfcBuildingStorey", err);
    }
  }

  /** Match main/primary and secondary (also major/minor) grid grouping labels. */
  const IFC_GRID_GROUP_RE = /main|primary|sec(ondary)?|maj(or)?|min(or)?/i;

  function normalizeIfcCat(raw: string): string {
    return stripIfcPrefix(raw).toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function isIfcGridCat(raw: string): boolean {
    return normalizeIfcCat(raw) === "GRID";
  }

  function isIfcGridAxisCat(raw: string): boolean {
    return normalizeIfcCat(raw) === "GRIDAXIS";
  }

  function ifcGridLabelBlob(item: Record<string, unknown>): string {
    return [
      valueToString(item["Name"]),
      valueToString(item["Tag"]),
      valueToString(item["AxisTag"]),
      valueToString(item["Description"]),
    ].join(" ");
  }

  /**
   * Show the IFC file's own grid lines (IfcGrid + IfcGridAxis).
   * Prefer main/primary + secondary (major/minor) when labels indicate grouping;
   * otherwise show all IfcGrid / IfcGridAxis (U/V style). Never hides them.
   */
  async function showIfcFileGrids() {
    const maps: OBC.ModelIdMap[] = [];
    try {
      const fromClassifier = await classifier.find({
        Categories: ["IFCGRID", "IfcGrid", "IfcGridAxis", "IFCGRIDAXIS"],
      });
      if (modelIdMapSize(fromClassifier) > 0) maps.push(fromClassifier);
    } catch (err) {
      console.warn("showIfcFileGrids classifier", err);
    }
    for (const [modelId, model] of fragments.list) {
      try {
        const cats = await model.getItemsOfCategories([
          /IFCGRID$/i,
          /IfcGrid$/i,
          /IFCGRIDAXIS$/i,
          /IfcGridAxis$/i,
        ]);
        const ids = new Set<number>();
        for (const list of Object.values(cats)) {
          for (const id of list) ids.add(id);
        }
        if (ids.size) maps.push({ [modelId]: ids });
      } catch (err) {
        console.warn("showIfcFileGrids getItemsOfCategories", modelId, err);
      }
    }
    if (!maps.length) {
      console.info("showIfcFileGrids: no IfcGrid / IfcGridAxis entities found");
      return;
    }

    const candidates: OBC.ModelIdMap = {};
    for (const m of maps) {
      for (const [mid, ids] of Object.entries(m)) {
        let set = candidates[mid];
        if (!set) {
          set = new Set<number>();
          candidates[mid] = set;
        }
        for (const id of ids) set.add(id);
      }
    }

    type AxisRec = { id: number; grouped: boolean };
    const perModel: {
      modelId: string;
      grids: Set<number>;
      axes: AxisRec[];
    }[] = [];
    let foundGroupedLabels = false;
    const BATCH = 200;

    for (const [modelId, localIds] of Object.entries(candidates)) {
      const model = fragments.list.get(modelId);
      if (!model || !localIds.size) continue;
      const grids = new Set<number>();
      const axes: AxisRec[] = [];
      const idList = [...localIds];
      for (let i = 0; i < idList.length; i += BATCH) {
        const slice = idList.slice(i, i + BATCH);
        let data: unknown[] = [];
        try {
          data = await model.getItemsData(slice, { attributesDefault: true });
        } catch (err) {
          console.warn("showIfcFileGrids getItemsData", modelId, err);
          for (const id of slice) grids.add(id);
          continue;
        }
        for (let j = 0; j < slice.length; j++) {
          const id = slice[j]!;
          const item = data[j];
          if (!item || typeof item !== "object") {
            grids.add(id);
            continue;
          }
          const rec = item as Record<string, unknown>;
          const cat = valueToString(rec["_category"] ?? rec["type"] ?? "");
          const grouped = IFC_GRID_GROUP_RE.test(ifcGridLabelBlob(rec));
          if (isIfcGridAxisCat(cat) || /GRIDAXIS/i.test(cat)) {
            if (grouped) foundGroupedLabels = true;
            axes.push({ id, grouped });
          } else if (isIfcGridCat(cat) || (/GRID/i.test(cat) && !/AXIS/i.test(cat))) {
            grids.add(id);
          } else if (!cat) {
            // Classifier hit without category — treat as grid parent
            grids.add(id);
          }
          // else: skip unrelated scraps
        }
      }
      perModel.push({ modelId, grids, axes });
    }

    const merged: OBC.ModelIdMap = {};
    for (const { modelId, grids, axes } of perModel) {
      const keep = new Set<number>(grids);
      if (foundGroupedLabels) {
        for (const a of axes) {
          if (a.grouped) keep.add(a.id);
        }
      } else {
        for (const a of axes) keep.add(a.id);
      }
      if (keep.size) merged[modelId] = keep;
    }

    const n = modelIdMapSize(merged);
    if (!n) {
      console.info("showIfcFileGrids: nothing left after main/secondary filter");
      return;
    }
    try {
      await hider.set(true, merged);
      console.info(
        `Shown ${n} IFC grid entity(ies)` +
          (foundGroupedLabels
            ? " (filtered to main/primary + secondary/major/minor labels)"
            : " (all IfcGrid + IfcGridAxis; no main/secondary labels)"),
      );
    } catch (err) {
      console.warn("showIfcFileGrids hider", err);
    }
  }

  function harvestMarksFromItem(
    item: unknown,
    modelId: string,
    fallbackLocalId: number | null,
    into: HarvestedMark[],
  ) {
    if (!item || typeof item !== "object") return;
    const data = item as Record<string, unknown>;
    const localId = extractLocalId(item) ?? fallbackLocalId;
    if (localId == null) return;
    const category = valueToString(data["_category"]);

    const candidates: string[] = [];
    for (const key of ["Tag", "Mark"]) {
      const v = valueToString(data[key]).trim();
      if (v) candidates.push(v);
    }

    const psets = data["IsDefinedBy"] ?? data["HasPropertySets"];
    if (Array.isArray(psets)) {
      for (const pset of psets) {
        if (!pset || typeof pset !== "object") continue;
        const ps = pset as Record<string, unknown>;
        const props = ps["HasProperties"] ?? ps["properties"];
        if (Array.isArray(props)) {
          for (const prop of props) {
            if (!prop || typeof prop !== "object") continue;
            const pr = prop as Record<string, unknown>;
            const n = valueToString(pr["Name"] ?? pr["name"]);
            if (!lookLikeMarkKey(n)) continue;
            const v = valueToString(
              pr["NominalValue"] ?? pr["value"] ?? pr["Value"] ?? "",
            ).trim();
            if (v) candidates.push(v);
          }
        } else {
          for (const [k, v] of Object.entries(ps)) {
            if (!lookLikeMarkKey(k)) continue;
            const s = valueToString(v).trim();
            if (s) candidates.push(s);
          }
        }
      }
    }

    for (const mark of candidates) {
      if (!mark || mark.length > 64) continue;
      if (/^IFC/i.test(mark)) continue;
      into.push({ mark, modelId, localId, category });
    }
  }

  async function collectPieceMarks() {
    const parentIds = new Set<number>();
    const tops: MemberNode[] = [];
    const BATCH = 200;

    for (const [modelId, model] of fragments.list) {
      let geoIds: number[] = [];
      try {
        geoIds = await model.getItemsIdsWithGeometry();
      } catch (err) {
        console.warn("getItemsIdsWithGeometry", err);
        continue;
      }
      // Supplement: ElementAssembly category (shop assemblies often have no own geometry)
      const assemblySeedIds = new Set<number>();
      try {
        const cats = await model.getItemsOfCategories([/ElementAssembly/i]);
        for (const ids of Object.values(cats)) {
          for (const id of ids) assemblySeedIds.add(id);
        }
      } catch (err) {
        console.warn("getItemsOfCategories ElementAssembly", err);
      }

      // Climb from every geometry item (+ assembly seeds) to highest non-spatial parent
      const climbSeeds = new Set<number>(geoIds);
      for (const id of assemblySeedIds) climbSeeds.add(id);

      let climbedRoots = new Set<number>();
      let climbedToRoots = new Set<number>();
      try {
        const detailed = await resolveAssemblyRootsDetailed({ [modelId]: climbSeeds });
        climbedRoots = detailed.roots[modelId] ?? new Set<number>();
        climbedToRoots = detailed.climbedTo[modelId] ?? new Set<number>();
      } catch (err) {
        console.warn("climb roots", err);
      }

      const candidateRoots = new Set<number>([...climbedRoots, ...assemblySeedIds]);
      if (!candidateRoots.size) continue;

      type RootInfo = {
        localId: number;
        mark: string;
        hasChildren: boolean;
        isAssemblySeed: boolean;
      };
      const byId = new Map<number, RootInfo>();
      const rootList = [...candidateRoots];

      for (let i = 0; i < rootList.length; i += BATCH) {
        const slice = rootList.slice(i, i + BATCH);
        try {
          const data = await model.getItemsData(slice, {
            attributesDefault: true,
            relations: {
              IsDefinedBy: { attributes: true, relations: true },
              IsDecomposedBy: { attributes: false, relations: false },
              IsNestedBy: { attributes: false, relations: false },
            },
          });
          for (let j = 0; j < data.length; j++) {
            const item = data[j];
            const id = extractLocalId(item) ?? slice[j]!;
            if (!item || typeof item !== "object") {
              byId.set(id, {
                localId: id,
                mark: `#${id}`,
                hasChildren: false,
                isAssemblySeed: assemblySeedIds.has(id),
              });
              continue;
            }
            const rec = item as Record<string, unknown>;
            const cat = valueToString(rec["_category"]);
            const hasRelKids =
              relationEntries(rec, "IsDecomposedBy").length > 0 ||
              relationEntries(rec, "IsNestedBy").length > 0;
            byId.set(id, {
              localId: id,
              mark: labelForItem(rec, id),
              hasChildren: hasRelKids,
              isAssemblySeed:
                assemblySeedIds.has(id) || categoryKey(cat) === "ELEMENTASSEMBLY",
            });
          }
        } catch (err) {
          console.warn("root label batch", err);
          for (const id of slice) {
            if (!byId.has(id)) {
              byId.set(id, {
                localId: id,
                mark: `#${id}`,
                hasChildren: false,
                isAssemblySeed: assemblySeedIds.has(id),
              });
            }
          }
        }
      }

      // Probe children so + expand is accurate for childless-looking roots
      const probe = [...byId.values()].filter((r) => !r.hasChildren).slice(0, 80);
      await Promise.all(
        probe.map(async (r) => {
          try {
            const ck = await model.getItemsChildren([r.localId]);
            if (ck.length) r.hasChildren = true;
          } catch {
            /* ignore */
          }
        }),
      );

      for (const r of byId.values()) {
        const wasClimbedTo = climbedToRoots.has(r.localId);
        // Top rows = climbed-to parents, ElementAssembly seeds, or self-roots that have kids.
        // Never list bare leaf parts (geometry self-root with no children).
        if (!wasClimbedTo && !r.isAssemblySeed && !r.hasChildren) continue;

        const canExpand = r.hasChildren || r.isAssemblySeed || wasClimbedTo;
        if (canExpand) parentIds.add(r.localId);

        tops.push({
          mark: r.mark,
          modelId,
          localId: r.localId,
          children: null,
          hasChildren: canExpand,
        });
      }
    }

    // Dedupe by modelId:localId
    const seen = new Set<string>();
    const uniqueTops: MemberNode[] = [];
    for (const t of tops) {
      const k = memberKey(t);
      if (seen.has(k)) continue;
      seen.add(k);
      uniqueTops.push(t);
    }

    knownParentIds = parentIds;
    expandedMemberKeys.clear();
    marksQuery = "";
    if (el.marksSearch) el.marksSearch.value = "";
    setMembersCollapsed(true);

    pieceMarks = uniqueTops.sort((a, b) =>
      a.mark.localeCompare(b.mark, undefined, { numeric: true, sensitivity: "base" }),
    );

    renderMarks();
  }



  function rootKeyFromMap(roots: OBC.ModelIdMap): string | null {
    for (const [modelId, ids] of Object.entries(roots)) {
      for (const id of ids) return `${modelId}:${id}`;
    }
    return null;
  }

  /** True if selection includes any id other than the given root. */
  function selectionHasNonRoot(sel: OBC.ModelIdMap, rootKey: string): boolean {
    for (const [modelId, ids] of Object.entries(sel)) {
      for (const id of ids) {
        if (`${modelId}:${id}` !== rootKey) return true;
      }
    }
    return false;
  }

  async function fetchItemsData(map: OBC.ModelIdMap): Promise<unknown[]> {
    const promises: Promise<unknown[]>[] = [];
    for (const [modelId, localIds] of Object.entries(map)) {
      const model = fragments.list.get(modelId);
      if (!model || !localIds.size) continue;
      promises.push(
        model.getItemsData([...localIds], {
          attributesDefault: true,
          relations: {
            IsDefinedBy: { attributes: true, relations: true },
          },
        }),
      );
    }
    return (await Promise.all(promises)).flat();
  }

  async function highlightMark(mark: PieceMark) {
    activeCategory = null;
    const map: OBC.ModelIdMap = {
      [mark.modelId]: new Set([mark.localId]),
    };
    const roots = await resolveAssemblyRoots(map);
    const expanded = await expandRelated(roots);
    const rootKey = rootKeyFromMap(roots);
    const alreadyActive = rootKey != null && activeRootKey === rootKey;
    const inspectChild =
      alreadyActive && rootKey != null && selectionHasNonRoot(map, rootKey);

    // Prefer top assembly mark in Members list when root is known
    let rootLocalId = mark.localId;
    const rootSet = roots[mark.modelId];
    if (rootSet && rootSet.size) {
      rootLocalId = [...rootSet][0]!;
    }
    const rootNode =
      findMemberNode(pieceMarks, mark.modelId, rootLocalId) ??
      findMemberNode(pieceMarks, mark.modelId, mark.localId);
    if (rootNode) {
      activeMark = rootNode.mark;
      activeMemberKey = memberKey(rootNode);
    } else {
      activeMark = mark.mark;
      activeMemberKey = memberKey(mark);
    }
    activeRootKey = rootKey ?? memberKey(mark);
    renderMarks();

    await applyHighlight(expanded, { zoom: true, rootMap: roots });
    // No bottom-left status after load (props panel is enough).

    // Two-step props: first pick → root; re-click child of active root → child
    try {
      const propData = await fetchItemsData(inspectChild ? map : roots);
      renderProperties(propData);
    } catch (err) {
      console.warn("mark properties", err);
    }
  }

  highlighter.events.select.onHighlight.add(async (modelIdMap) => {
    lastSelection = cloneModelIdMap(modelIdMap);
    setActiveHighlight(modelIdMap);
    setHlButtons(true);
    try {
      const roots = await resolveAssemblyRoots(modelIdMap);
      const expanded = await expandRelated(roots);
      const rootKey = rootKeyFromMap(roots);
      const alreadyActive = rootKey != null && activeRootKey === rootKey;
      const inspectChild =
        alreadyActive && rootKey != null && selectionHasNonRoot(modelIdMap, rootKey);

      // Soft-highlight full assembly with per-part colors (keep select on click)
      if (modelIdMapSize(expanded) > 0) {
        await applyAssemblyColors(expanded, { zoom: false, rootMap: roots });
        setActiveHighlight(expanded);
      } else {
        await clearEccHighlights();
      }

      // Sync Members active state to top assembly root + track activeRootKey
      let syncedRoot = false;
      for (const [modelId, rootIds] of Object.entries(roots)) {
        const rootId = [...rootIds][0];
        if (rootId == null) continue;
        const rootNode = findMemberNode(pieceMarks, modelId, rootId);
        if (rootNode) {
          activeMark = rootNode.mark;
          activeMemberKey = memberKey(rootNode);
          activeCategory = null;
          syncedRoot = true;
          renderMarks();
          break;
        }
      }
      activeRootKey = rootKey;
      if (!syncedRoot && rootKey) {
        // Still track root id even if not in Members list
        activeMemberKey = rootKey;
      }

      // Two-step props: first pick → root; child of active root → clicked item
      const data = await fetchItemsData(inspectChild ? modelIdMap : roots);

      // Status climb label still refers to the clicked child
      let childMark = "";
      for (const [modelId, localIds] of Object.entries(modelIdMap)) {
        for (const id of localIds) {
          const node = findMemberNode(pieceMarks, modelId, id);
          if (node?.mark) {
            childMark = node.mark;
            break;
          }
        }
        if (childMark) break;
      }
      if (!childMark) {
        try {
          for (const item of data) {
            if (!item || typeof item !== "object") continue;
            // Only use mark from clicked props when inspecting child
            if (inspectChild) {
              childMark = firstMarkCandidate(item as Record<string, unknown>);
              if (childMark) break;
            }
          }
          if (!childMark) {
            const childData = await fetchItemsData(modelIdMap);
            for (const item of childData) {
              if (!item || typeof item !== "object") continue;
              childMark = firstMarkCandidate(item as Record<string, unknown>);
              if (childMark) break;
            }
          }
        } catch {
          /* ignore — status label only */
        }
      }
      const n = modelIdMapSize(expanded);
      const climbed =
        modelIdMapSize(roots) > 0 &&
        ![...Object.entries(roots)].every(([mid, rids]) => {
          const sel = modelIdMap[mid];
          if (!sel) return false;
          return [...rids].every((id) => sel.has(id)) && rids.size === sel.size;
        });
      // Selection feedback lives in the props panel — keep status hidden after load.
      renderProperties(data);
    } catch (err) {
      console.warn("properties", err);
      el.propsBody.innerHTML = `<p class="muted">Could not load properties.</p>`;
      el.propsActions.hidden = false;
      showProps(true);
    }
  });

  highlighter.events.select.onClear.add(() => {
    lastSelection = {};
    activeRootKey = null;
    el.propsBody.innerHTML = `<p class="muted">Click an item to inspect.</p>`;
    el.propsActions.hidden = true;
  });

  el.btnPropsClose.addEventListener("click", () => showProps(false));

  function showLegal(show: boolean) {
    el.legalPanel.hidden = !show;
  }
  el.btnLegal.addEventListener("click", () => showLegal(true));
  el.btnLegalClose.addEventListener("click", () => showLegal(false));
  el.legalPanel.addEventListener("click", (ev) => {
    if (ev.target === el.legalPanel) showLegal(false);
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape") return;
    if (!el.legalPanel.hidden) showLegal(false);
  });



  el.membersToggle.addEventListener("click", () => {
    const collapsed = !el.membersSection.classList.contains("collapsed");
    setMembersCollapsed(collapsed);
  });

  el.marksSearch.addEventListener("input", () => {
    marksQuery = el.marksSearch.value;
    // Auto-expand matching parents that already have loaded children
    const q = marksQuery.trim().toLowerCase();
    if (q) {
      for (const top of pieceMarks) {
        if (top.children?.some((c) => nodeMatchesQuery(c, q))) {
          expandedMemberKeys.add(memberKey(top));
        }
      }
    }
    renderMarks();
  });

  el.marksList.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    const expandBtn = target.closest("button[data-expand]") as HTMLButtonElement | null;
    if (expandBtn?.dataset.expand) {
      ev.preventDefault();
      ev.stopPropagation();
      const [modelId, idStr] = expandBtn.dataset.expand.split(":");
      const localId = Number(idStr);
      if (!modelId || !Number.isFinite(localId)) return;
      void toggleMemberExpand(modelId, localId);
      return;
    }
    const btn = target.closest("button[data-mark]") as HTMLButtonElement | null;
    if (!btn?.dataset.mark || !btn.dataset.model || !btn.dataset.id) return;
    const localId = Number(btn.dataset.id);
    if (!Number.isFinite(localId)) return;
    void highlightMark({
      mark: btn.dataset.mark,
      modelId: btn.dataset.model,
      localId,
    });
  });

  el.btnClearHl.addEventListener("click", () => {
    void (async () => {
      await clearHighlights();
      setStatus("");
    })();
  });

  el.btnShowAll.addEventListener("click", () => {
    void showAll();
  });

  el.btnIsolate.addEventListener("click", () => {
    void isolateMap(
      modelIdMapSize(activeHighlight) > 0 ? activeHighlight : lastSelection,
    );
  });

  el.btnIsolateSel.addEventListener("click", () => {
    const map =
      modelIdMapSize(activeHighlight) > 0
        ? activeHighlight
        : modelIdMapSize(lastSelection) > 0
          ? lastSelection
          : {};
    void isolateMap(map);
  });


  async function clearModels() {
    const ids = [...fragments.list.keys()];
    for (const id of ids) {
      try {
        await fragments.core.disposeModel(id);
      } catch (err) {
        console.warn("disposeModel", id, err);
      }
    }
    try {
      await highlighter.clear();
    } catch {
      /* ignore */
    }
    try {
      classifier.list.clear();
    } catch {
      /* ignore */
    }
    pieceMarks = [];
    activeHighlight = {};
    lastSelection = {};
    activeCategory = null;
    activeMark = null;
    activeMemberKey = null;
    activeRootKey = null;
    expandedMemberKeys.clear();
    knownParentIds = new Set();
    marksQuery = "";
    if (el.marksSearch) el.marksSearch.value = "";
    setMembersCollapsed(true);
    renderMarks();
    clearEccModelGrids();
    pendingIfcBytes = null;
    fileLengthUnit = "METRE";
    showTools(false);
    setHlButtons(false);
  }

  async function fitView() {
    try {
      const box = new THREE.Box3();
      let has = false;
      for (const [, model] of fragments.list) {
        const b = model.object
          ? new THREE.Box3().setFromObject(model.object)
          : null;
        if (b && !b.isEmpty()) {
          box.union(b);
          has = true;
        }
      }
      if (!has || box.isEmpty()) {
        return;
      }
      const sphere = new THREE.Sphere();
      box.getBoundingSphere(sphere);
      await world.camera.controls.fitToSphere(sphere, true);
    } catch (err) {
      console.warn("fit", err);
    }
    // Keep Fragments IFC grids; do not rebuild/dispose on fit.
  }

  async function finishLoaded(name: string, fromCache: boolean) {
    await fitView();
    showTools(true);
    setMembersCollapsed(true);
    setHlButtons(true);
    setStatus(
      fromCache
        ? `Loaded ${name} (cached) · classifying…`
        : `Loaded ${name} · classifying…`,
      "busy",
    );
    // On phones, keep props closed so they do not cover the canvas until selection.
    const narrow = window.matchMedia("(max-width: 720px)").matches;
    if (narrow) {
      showProps(false);
    } else {
      showProps(true);
      el.propsBody.innerHTML = `<p class="muted">Click an item to inspect.</p>`;
      el.propsActions.hidden = true;
    }
    try {
      await classifyModel();
      // Optional: try unhiding fragment grid entities (usually no visible meshes).
      try {
        await showIfcFileGrids();
      } catch (err) {
        console.warn("showIfcFileGrids", err);
      }
      // Members first — never block the UI on grids (getGrids can hang).
      try {
        await collectPieceMarks();
      } catch (err) {
        console.warn("collectPieceMarks", err);
      }
      setStatus("");
      void showEccModelGrids().catch((err) =>
        console.warn("showEccModelGrids", err),
      );
    } catch (err) {
      console.warn("post-load classify", err);
      try {
        await collectPieceMarks();
      } catch (marksErr) {
        console.warn("collectPieceMarks", marksErr);
      }
      setStatus("");
      void showEccModelGrids().catch((gridErr) =>
        console.warn("showEccModelGrids", gridErr),
      );
    }
  }


  /** POST converted .frag next to shipped .ifc so next visitor skips convert. */
  async function uploadShippedFrag(
    ifcFilename: string,
    fragAb: ArrayBuffer,
  ): Promise<void> {
    const fragName = ifcFilename.replace(/\.ifc$/i, ".frag");
    if (!/^[A-Za-z0-9._-]+\.frag$/.test(fragName)) {
      console.warn("uploadShippedFrag: bad frag name", fragName);
      return;
    }
    const base = import.meta.env.BASE_URL;
    const urls = [
      `${base}models/save-frag.php?f=${encodeURIComponent(fragName)}`,
      `${base}model/save-frag.php?f=${encodeURIComponent(fragName)}`,
    ];
    const body = fragAb;
    for (const url of urls) {
      try {
        const ctrl = new AbortController();
        const t = window.setTimeout(() => ctrl.abort(), 30_000);
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/octet-stream" },
          body,
          signal: ctrl.signal,
        });
        window.clearTimeout(t);
        if (res.ok) {
          console.info("uploadShippedFrag ok", url, fragName);
          return;
        }
        console.warn("uploadShippedFrag fail", url, res.status);
      } catch (err) {
        console.warn("uploadShippedFrag", url, err);
      }
    }
  }

  async function loadFragBuffer(frag: ArrayBuffer, modelId: string) {
    await fragments.core.load(frag, { modelId });
  }

  async function loadIfcBuffer(buffer: Uint8Array, name: string) {
    const gen = ++loadGen;
    const modelId = name.replace(/\.ifc$/i, "");
    showWelcome(false);

    try {
      await clearModels();
      if (gen !== loadGen) return;

      // Keep IFC bytes for grid fallback (including cached-frag path).
      try {
        pendingIfcBytes =
          buffer.byteOffset === 0 &&
          buffer.byteLength === buffer.buffer.byteLength
            ? buffer
            : buffer.slice();
        const head = new TextDecoder("utf-8", { fatal: false }).decode(
          pendingIfcBytes.subarray(
            0,
            Math.min(pendingIfcBytes.byteLength, 512_000),
          ),
        );
        // Unit assignment is usually near the end — also peek the tail.
        const tailStart = Math.max(0, pendingIfcBytes.byteLength - 256_000);
        const tail = new TextDecoder("utf-8", { fatal: false }).decode(
          pendingIfcBytes.subarray(tailStart),
        );
        fileLengthUnit = detectLengthUnitFromIfcText(tail + "\n" + head);
      } catch (err) {
        console.warn("pendingIfcBytes / unit detect", err);
        pendingIfcBytes = buffer.slice();
        fileLengthUnit = "METRE";
      }

      const key = await cacheKeyForIfc(name, buffer);
      const cached = await idbGet(key);
      if (gen !== loadGen) return;

      if (cached && cached.byteLength > 0) {
        setStatus("Loading cached model…", "busy");
        await loadFragBuffer(cached, modelId);
        if (gen !== loadGen) return;
        await finishLoaded(name, true);
        return;
      }

      setStatus("Converting IFC (first time)…", "busy");
      const bytes =
        buffer.byteOffset === 0 && buffer.byteLength === buffer.buffer.byteLength
          ? buffer
          : buffer.slice();

      const model = await ifcLoader.load(bytes, false, modelId, {
        processData: {
          progressCallback: (p: number) => {
            if (gen !== loadGen) return;
            const pct = Math.round((p || 0) * 100);
            setStatus(`Converting IFC (first time)… ${pct}%`, "busy");
          },
        },
      });
      if (gen !== loadGen) return;

      let fragAb: ArrayBuffer | null = null;
      try {
        fragAb = await model.getBuffer(false);
        await idbPut(key, fragAb);
      } catch (err) {
        console.warn("frag cache store failed", err);
      }

      // Ship .frag next to .ifc for other users (only after fresh convert).
      // Fire-and-forget after finishLoaded starts — each POST aborts at 30s.
      const shipFrag =
        fragAb && fragAb.byteLength > 0
          ? uploadShippedFrag(name, fragAb)
          : null;

      await finishLoaded(name, false);

      if (shipFrag) {
        void withTimeout(shipFrag, 30_000, "uploadShippedFrag");
      }
    } catch (err) {
      console.error(err);
      setStatus(`Failed to load ${name}`, "error");
      showWelcome(true);
      throw err;
    }
  }

  function urlCandidatesFor(filename: string): string[] {
    const base = import.meta.env.BASE_URL;
    // Prefer direct files (get.php hits Bluehost bot-challenge 409 from some clients).
    return [
      `${base}models/${encodeURIComponent(filename)}`,
      `${base}model/${encodeURIComponent(filename)}`,
      `${base}models/get.php?f=${encodeURIComponent(filename)}`,
      `${base}model/get.php?f=${encodeURIComponent(filename)}`,
      `${base}${encodeURIComponent(filename)}`,
    ];
  }

  async function fetchFirstOk(
    candidates: string[],
  ): Promise<{ res: Response; used: string } | null> {
    for (const url of candidates) {
      try {
        const r = await fetch(url);
        if (r.ok) return { res: r, used: url };
      } catch (err) {
        console.warn("fetch miss", url, err);
      }
    }
    return null;
  }

  function adoptPendingIfcBytes(buffer: Uint8Array): void {
    try {
      pendingIfcBytes =
        buffer.byteOffset === 0 &&
        buffer.byteLength === buffer.buffer.byteLength
          ? buffer
          : buffer.slice();
      const head = new TextDecoder("utf-8", { fatal: false }).decode(
        pendingIfcBytes.subarray(
          0,
          Math.min(pendingIfcBytes.byteLength, 512_000),
        ),
      );
      const tailStart = Math.max(0, pendingIfcBytes.byteLength - 256_000);
      const tail = new TextDecoder("utf-8", { fatal: false }).decode(
        pendingIfcBytes.subarray(tailStart),
      );
      fileLengthUnit = detectLengthUnitFromIfcText(tail + "\n" + head);
    } catch (err) {
      console.warn("pendingIfcBytes / unit detect", err);
      pendingIfcBytes = buffer.slice();
      fileLengthUnit = "METRE";
    }
  }

  async function loadFromUrl(filename: string) {
    const fragName = filename.replace(/\.ifc$/i, ".frag");
    const modelId = filename.replace(/\.ifc$/i, "");
    const fragCandidates = urlCandidatesFor(fragName);
    const ifcCandidates = urlCandidatesFor(filename);

    setStatus(`Fetching ${filename}…`, "busy");
    showWelcome(false);

    // 1) Prefer shipped .frag (same folder as .ifc) — skip slow client convert.
    const fragHit = await fetchFirstOk(fragCandidates);
    if (fragHit) {
      const gen = ++loadGen;
      try {
        await clearModels();
        if (gen !== loadGen) return;

        // Fetch IFC in parallel for grid fallback (do not block mesh on it).
        const ifcPromise = (async (): Promise<Uint8Array | null> => {
          try {
            const hit = await fetchFirstOk(ifcCandidates);
            if (!hit) return null;
            const ab = await hit.res.arrayBuffer();
            console.info("IFC (grids) fetched from", hit.used);
            return new Uint8Array(ab);
          } catch (err) {
            console.warn("IFC fetch for grids", err);
            return null;
          }
        })();

        setStatus("Loading model…", "busy");
        const fragAb = await fragHit.res.arrayBuffer();
        if (gen !== loadGen) return;
        console.info("FRAG fetched from", fragHit.used);
        await loadFragBuffer(fragAb, modelId);
        if (gen !== loadGen) return;

        // Attach IFC when ready; re-try fallback grids if none shown yet.
        void ifcPromise
          .then((bytes) => {
            if (gen !== loadGen || !bytes || bytes.byteLength < 32) return;
            adoptPendingIfcBytes(bytes);
            if (eccModelGridGroups.length === 0) {
              try {
                showEccFallbackIfcGrids();
              } catch (err) {
                console.warn("late IFC grid fallback", err);
              }
            }
          })
          .catch((err) => console.warn("late IFC attach", err));

        // Brief wait so pendingIfcBytes is often ready before getGrids timeout/fallback.
        const earlyIfc = await withTimeout(ifcPromise, 2500, "IFC early for grids");
        if (gen !== loadGen) return;
        if (earlyIfc && earlyIfc.byteLength >= 32) {
          adoptPendingIfcBytes(earlyIfc);
        }

        await finishLoaded(filename, false);
      } catch (err) {
        console.error(err);
        setStatus(`Failed to load ${filename}`, "error");
        showWelcome(true);
      }
      return;
    }

    // 2) No .frag — existing IFC convert + IndexedDB cache path.
    const ifcHit = await fetchFirstOk(ifcCandidates);
    if (!ifcHit) {
      setStatus(
        `Model not found: put ${filename} (and optional .frag) in /bim/model/ or /bim/models/`,
        "error",
      );
      showWelcome(true);
      return;
    }
    const data = await ifcHit.res.arrayBuffer();
    const buffer = new Uint8Array(data);
    console.info("IFC fetched from", ifcHit.used);
    await loadIfcBuffer(buffer, filename);
  }

  async function loadFromFile(file: File) {
    const safe = sanitizeIfcFilename(file.name);
    if (!safe) {
      setStatus("Invalid IFC filename (use simple A-Z, 0-9, ._-)", "error");
      return;
    }
    const data = await file.arrayBuffer();
    const buffer = new Uint8Array(data);
    await loadIfcBuffer(buffer, safe);
  }

  el.welcomeFile.addEventListener("change", () => {
    const f = el.welcomeFile.files?.[0];
    if (f) void loadFromFile(f);
    el.welcomeFile.value = "";
  });
  el.btnFit.addEventListener("click", () => void fitView());

  const qf = getQueryFilename();
  if (qf) {
    const raw = new URLSearchParams(window.location.search).get("f") ?? "";
    if (raw && !sanitizeIfcFilename(raw)) {
      setStatus("Rejected filename (path traversal or invalid chars)", "error");
      showWelcome(true);
      return;
    }
    await loadFromUrl(qf);
  } else {
    const raw = new URLSearchParams(window.location.search).get("f");
    if (raw != null && raw !== "") {
      setStatus("Rejected filename (path traversal or invalid chars)", "error");
    } else {
      setStatus("No model — open a local IFC or use ?f=file.ifc");
    }
    showWelcome(true);
  }
}

main().catch((err) => {
  console.error(err);
  setStatus("Viewer failed to start — see console", "error");
  showWelcome(true);
});
